import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useBookCovers } from '../../hooks/useBookCovers'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { format } from 'date-fns'
import toast from 'react-hot-toast'

const { FiTrash2, FiEdit3, FiDownload, FiEye, FiSearch, FiFilter } = FiIcons

const BookLibrary = ({ onSelectCover }) => {
  const { covers, loading, deleteCover } = useBookCovers()
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('newest')
  const [selectedCover, setSelectedCover] = useState(null)

  const filteredCovers = covers
    .filter(cover => 
      cover.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cover.author.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at)
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at)
        case 'title':
          return a.title.localeCompare(b.title)
        case 'author':
          return a.author.localeCompare(b.author)
        default:
          return 0
      }
    })

  const handleDelete = async (cover) => {
    if (window.confirm(`Are you sure you want to delete "${cover.title}"?`)) {
      const success = await deleteCover(cover.id, cover.file_path)
      if (success) {
        toast.success('Book cover deleted')
      } else {
        toast.error('Failed to delete cover')
      }
    }
  }

  const handleSelect = (cover) => {
    setSelectedCover(cover)
    onSelectCover(cover.file_url, {
      title: cover.title,
      author: cover.author,
      id: cover.id
    })
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          Your Book Library ({covers.length})
        </h3>
        
        <div className="flex space-x-3">
          {/* Search */}
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search covers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Sort */}
          <div className="relative">
            <SafeIcon icon={FiFilter} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="title">Title A-Z</option>
              <option value="author">Author A-Z</option>
            </select>
          </div>
        </div>
      </div>

      {filteredCovers.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiSearch} className="w-8 h-8 text-gray-400" />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">No covers found</h4>
          <p className="text-gray-600">
            {searchTerm ? 'Try a different search term' : 'Upload your first book cover to get started'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          <AnimatePresence>
            {filteredCovers.map((cover) => (
              <motion.div
                key={cover.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                whileHover={{ scale: 1.05 }}
                className={`
                  relative group bg-gray-50 rounded-lg overflow-hidden cursor-pointer border-2 transition-all
                  ${selectedCover?.id === cover.id 
                    ? 'border-primary-500 shadow-lg' 
                    : 'border-transparent hover:border-primary-300'
                  }
                `}
                onClick={() => handleSelect(cover)}
              >
                {/* Cover Image */}
                <div className="aspect-[3/4] bg-gray-200">
                  <img
                    src={cover.file_url}
                    alt={cover.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/api/placeholder/200/300'
                    }}
                  />
                </div>

                {/* Overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200">
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleSelect(cover)
                        }}
                        className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      >
                        <SafeIcon icon={FiEye} className="w-4 h-4 text-gray-700" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(cover)
                        }}
                        className="p-2 bg-red-500 rounded-full hover:bg-red-600 transition-colors"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-3 bg-white">
                  <h4 className="font-medium text-gray-900 text-sm truncate mb-1">
                    {cover.title}
                  </h4>
                  <p className="text-xs text-gray-600 truncate mb-2">
                    {cover.author || 'Unknown Author'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {format(new Date(cover.created_at), 'MMM d, yyyy')}
                  </p>
                </div>

                {/* Selected Indicator */}
                {selectedCover?.id === cover.id && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full" />
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  )
}

export default BookLibrary